import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../theme/utils';
import FilterContextVirtualTable from '../../../../components/Filter/FilterContextVirtualTable';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import FilterResultNoItemsScreen from '../../../../components/Filter/FilterResultNoItemsScreen';
import useFilterContext from '../../../../components/Filter/hooks/useFilterContext';
import {
  EmployeeOverviewCalculatedData,
  EmployeeOverviewSearchResultItem,
} from '../../../../models/TimeKeeping';
import { EmployeeContact } from '../../../../models/Employee';
import useExcelExport from '../../../../hooks/useExcelExport';
import { exportToCsv } from '../../../../util/export';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  cell: {
    display: 'flex',
    alignItems: 'center',
  },
  cellVerticalAlign: {
    justifyContent: 'center',
  },
}));

interface HRTimeKeepingOverviewPageTableProps {
  className?: string;
}

export const HRTimeKeepingOverviewPageTable: React.FC<
  HRTimeKeepingOverviewPageTableProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { data, isLoading } = useFilterContext<
    EmployeeContact,
    EmployeeOverviewCalculatedData
  >();

  const tableData = useMemo(
    () =>
      data?.items?.sort((a, b) => {
        const lastNameComparison = a.data.lastName.localeCompare(
          b.data.lastName
        );
        if (lastNameComparison !== 0) {
          return lastNameComparison; // Sort by last name if they are not equal
        }
        return a.data.firstName.localeCompare(b.data.firstName); // Fallback to first name if last names are equal
      }) || [],
    [data?.items]
  );

  const [selectedSearchItems, setSelectedSearchItems] = useState<
    EmployeeOverviewSearchResultItem[]
  >([]);

  const exportExcel = useExcelExport();
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onSelectionChange = (
    selectedItems: EmployeeOverviewSearchResultItem[]
  ) => {
    setSelectedSearchItems(selectedItems);
  };

  const handleOnExportToExcelClick = () => {
    const items =
      selectedSearchItems.length > 0 ? selectedSearchItems : data?.items;
    const overviewItems = items.map((item) => {
      return {
        [t('hr:timeAndLeaveManagement.overview.employee')]:
          `${item.data.firstName} ${item.data.lastName}`,
        [`${t('hr:timeAndLeaveManagement.overview.holidayEntitlements')} (${t(`absences:absenceOverview.popOver.days`)})`]: `${item.calculated.holidayEntitlements.toLocaleString()}`,
        [`${t('hr:timeAndLeaveManagement.overview.remainingLeave')} (${t(`absences:absenceOverview.popOver.days`)})`]: `${item.calculated.remainingLeave.toLocaleString()}`,
        [`${t('hr:timeAndLeaveManagement.overview.remainingLeaveLastYear', {
          lastYear: new Date().getFullYear() - 1,
        })} (${t(`absences:absenceOverview.popOver.days`)})`]:
          `${item.calculated.remainingLeaveLastYear.toLocaleString()}`,
        [`${t('hr:timeAndLeaveManagement.overview.paidOvertime')} (h)`]: `${item.calculated.paidOvertime.toLocaleString()}`,
        [`${t('hr:timeAndLeaveManagement.overview.currentOvertime')} (h)`]: `${item.calculated.currentOvertime.toLocaleString()}`,
        [`${t('hr:timeAndLeaveManagement.overview.unsettledOvertimeHours')} (h)`]: `${item.calculated.unsettledOvertimeHours.toLocaleString()}`,
        [`${t('hr:timeAndLeaveManagement.overview.sickDaysCurrentYear', {
          currentYear: new Date().getFullYear(),
        })} (${t(`absences:absenceOverview.popOver.days`)})`]:
          `${item.calculated.sickDaysCurrentYear.toLocaleString()}`,
        [`${t(
          'hr:timeAndLeaveManagement.overview.overtimeCompensationCurrentYear',
          {
            currentYear: new Date().getFullYear(),
          }
        )} (${t(`absences:absenceOverview.popOver.days`)})`]:
          `${item.calculated.overtimeCompensationCurrentYear.toLocaleString()}`,
        [`${t('hr:timeAndLeaveManagement.overview.approvedLeaveNextYear', {
          nextYear: new Date().getFullYear() + 1,
        })} (${t(`absences:absenceOverview.popOver.days`)})`]:
          `${item.calculated.approvedLeaveNextYear.toLocaleString()}`,
        [`${t(
          'hr:timeAndLeaveManagement.overview.overtimeCompensationNextYear',
          {
            nextYear: new Date().getFullYear() + 1,
          }
        )} (${t(`absences:absenceOverview.popOver.days`)})`]:
          `${item.calculated.overtimeCompensationNextYear.toLocaleString()}`,
      };
    });

    exportExcel(
      overviewItems,
      t('hr:timeAndLeaveManagement.overview.export.title')
    );
  };

  const handleOnExportToCsvClick = () => {
    const items =
      selectedSearchItems.length > 0 ? selectedSearchItems : data?.items;

    const headers = [
      t('hr:timeAndLeaveManagement.overview.employee'),
      `${t('hr:timeAndLeaveManagement.overview.holidayEntitlements')} (${t(
        `absences:absenceOverview.popOver.days`
      )})`,
      `${t('hr:timeAndLeaveManagement.overview.remainingLeave')} (${t(
        `absences:absenceOverview.popOver.days`
      )})`,
      `${t('hr:timeAndLeaveManagement.overview.remainingLeaveLastYear', {
        lastYear: new Date().getFullYear() - 1,
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t('hr:timeAndLeaveManagement.overview.paidOvertime')} (h)`,
      `${t('hr:timeAndLeaveManagement.overview.currentOvertime')} (h)`,
      `${t('hr:timeAndLeaveManagement.overview.unsettledOvertimeHours')} (h)`,
      `${t('hr:timeAndLeaveManagement.overview.sickDaysCurrentYear', {
        currentYear: new Date().getFullYear(),
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t(
        'hr:timeAndLeaveManagement.overview.overtimeCompensationCurrentYear',
        {
          currentYear: new Date().getFullYear(),
        }
      )} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t('hr:timeAndLeaveManagement.overview.approvedLeaveNextYear', {
        nextYear: new Date().getFullYear() + 1,
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t('hr:timeAndLeaveManagement.overview.overtimeCompensationNextYear', {
        nextYear: new Date().getFullYear() + 1,
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
    ];
    const csvData = items.map((item) => {
      return [
        `${item.data.firstName} ${item.data.lastName}`,
        `${item.calculated.holidayEntitlements.toLocaleString()}`,
        `${item.calculated.remainingLeave.toLocaleString()}`,
        `${item.calculated.remainingLeaveLastYear.toLocaleString()}`,
        `${item.calculated.paidOvertime.toLocaleString()}`,
        `${item.calculated.currentOvertime.toLocaleString()}`,
        `${item.calculated.unsettledOvertimeHours.toLocaleString()}`,
        `${item.calculated.sickDaysCurrentYear.toLocaleString()}`,
        `${item.calculated.overtimeCompensationCurrentYear.toLocaleString()}`,
        `${item.calculated.approvedLeaveNextYear.toLocaleString()}`,
        `${item.calculated.overtimeCompensationNextYear.toLocaleString()}`,
      ];
    });

    exportToCsv(
      headers,
      csvData,
      t('hr:timeAndLeaveManagement.overview.export.title')
    );
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  //#region ------------------------------ Columns
  const columns: Column<EmployeeOverviewSearchResultItem>[] = [
    {
      id: 'employee',
      width: 200,
      title: t('hr:timeAndLeaveManagement.overview.employee'),
      accessor: 'data.employeeContactId',
      sortingFn: (rowA, rowB) => {
        const contactNameA = `${rowA.data.firstName} ${rowA.data.lastName}`;
        const contactNameB = `${rowB.data.firstName} ${rowB.data.lastName}`;
        return contactNameA.localeCompare(contactNameB);
      },
      Cell: ({
        originalData: {
          data: { firstName, lastName },
        },
      }) => {
        return `${firstName} ${lastName}`;
      },
      alignSelf: true,
    },
    {
      id: 'holidayEntitlements',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.holidayEntitlements'),
      accessor: 'calculated.holidayEntitlements',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.holidayEntitlements -
        rowB.calculated.holidayEntitlements,
      Cell: ({
        originalData: {
          calculated: { holidayEntitlements },
        },
      }) => {
        const dayLabel = holidayEntitlements === 1 ? 'day' : 'days';
        return (
          <div>
            {`${holidayEntitlements.toLocaleString()}  ${t(`absences:absenceOverview.popOver.${dayLabel}`)}` ||
              '-'}
          </div>
        );
      },
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'remainingLeave',
      width: 120,
      title: t('hr:timeAndLeaveManagement.overview.remainingLeave'),
      accessor: 'calculated.remainingLeave',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.remainingLeave - rowB.calculated.remainingLeave,
      Cell: ({
        originalData: {
          calculated: { remainingLeave },
        },
      }) => {
        const dayLabel = remainingLeave === 1 ? 'day' : 'days';
        return (
          <div>
            {' '}
            {`${remainingLeave.toLocaleString()}  ${t(`absences:absenceOverview.popOver.${dayLabel}`)}` ||
              '-'}
          </div>
        );
      },
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'remainingLeaveLastYear',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.remainingLeaveLastYear', {
        lastYear: new Date().getFullYear() - 1,
      }),
      accessor: 'calculated.remainingLeaveLastYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.remainingLeaveLastYear -
        rowB.calculated.remainingLeaveLastYear,
      Cell: ({
        originalData: {
          calculated: { remainingLeaveLastYear },
        },
      }) => {
        const dayLabel = remainingLeaveLastYear === 1 ? 'day' : 'days';

        return (
          <div>
            {`${remainingLeaveLastYear.toLocaleString()}  ${t(`absences:absenceOverview.popOver.${dayLabel}`)}` ||
              '-'}
          </div>
        );
      },
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'paidOvertime',
      width: 200,
      title: t('hr:timeAndLeaveManagement.overview.paidOvertime'),
      accessor: 'calculated.paidOvertime',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.paidOvertime - rowB.calculated.paidOvertime,
      Cell: ({
        originalData: {
          calculated: { paidOvertime },
        },
      }) => <div> {`${paidOvertime.toLocaleString()} h` || '-'}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'currentOvertime',
      width: 200,
      title: t('hr:timeAndLeaveManagement.overview.currentOvertime'),
      accessor: 'calculated.currentOvertime',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.currentOvertime - rowB.calculated.currentOvertime,
      Cell: ({
        originalData: {
          calculated: { currentOvertime },
        },
      }) => (
        // parse to two decimal places but with , and only if there are some decimal
        <div>
          {`${
            currentOvertime.toString().includes('.')
              ? currentOvertime.toFixed(2).replace('.', ',')
              : currentOvertime || '0'
          } h`}
        </div>
      ),
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'unsettledOvertimeHours',
      width: 180,
      title: t('hr:timeAndLeaveManagement.overview.unsettledOvertimeHours'),
      accessor: 'calculated.unsettledOvertimeHours',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.unsettledOvertimeHours -
        rowB.calculated.unsettledOvertimeHours,
      Cell: ({
        originalData: {
          calculated: { unsettledOvertimeHours },
        },
      }) => (
        // parse to two decimal places but with , and only if there are some decimal
        <div>
          {`${
            unsettledOvertimeHours.toString().includes('.')
              ? unsettledOvertimeHours.toFixed(2).replace('.', ',')
              : unsettledOvertimeHours || '0'
          } h`}
        </div>
      ),
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'sickDaysCurrentYear',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.sickDaysCurrentYear', {
        currentYear: new Date().getFullYear(),
      }),
      accessor: 'calculated.sickDaysCurrentYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.sickDaysCurrentYear -
        rowB.calculated.sickDaysCurrentYear,
      Cell: ({
        originalData: {
          calculated: { sickDaysCurrentYear },
        },
      }) => {
        const dayLabel = sickDaysCurrentYear === 1 ? 'day' : 'days';
        return (
          <div>
            {' '}
            {`${sickDaysCurrentYear.toLocaleString()}  ${t(`absences:absenceOverview.popOver.${dayLabel}`)}` ||
              '-'}
          </div>
        );
      },
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'overtimeCompensationCurrentYear',
      width: 150,
      title: t(
        'hr:timeAndLeaveManagement.overview.overtimeCompensationCurrentYear',
        {
          currentYear: new Date().getFullYear(),
        }
      ),
      accessor: 'calculated.overtimeCompensationCurrentYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.overtimeCompensationCurrentYear -
        rowB.calculated.overtimeCompensationCurrentYear,
      Cell: ({
        originalData: {
          calculated: { overtimeCompensationCurrentYear },
        },
      }) => {
        const dayLabel = overtimeCompensationCurrentYear === 1 ? 'day' : 'days';

        return (
          <div>
            {' '}
            {`${overtimeCompensationCurrentYear.toLocaleString()}  ${t(`absences:absenceOverview.popOver.${dayLabel}`)}` ||
              '-'}
          </div>
        );
      },
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'approvedLeaveNextYear',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.approvedLeaveNextYear', {
        nextYear: new Date().getFullYear() + 1,
      }),
      accessor: 'calculated.approvedLeaveNextYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.approvedLeaveNextYear -
        rowB.calculated.approvedLeaveNextYear,
      Cell: ({
        originalData: {
          calculated: { approvedLeaveNextYear },
        },
      }) => {
        const dayLabel = approvedLeaveNextYear === 1 ? 'day' : 'days';

        return (
          <div>
            {`${approvedLeaveNextYear.toLocaleString()}  ${t(`absences:absenceOverview.popOver.${dayLabel}`)}` ||
              '-'}
          </div>
        );
      },
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'overtimeCompensationNextYear',
      width: 150,
      title: t(
        'hr:timeAndLeaveManagement.overview.overtimeCompensationNextYear',
        {
          nextYear: new Date().getFullYear() + 1,
        }
      ),
      accessor: 'calculated.overtimeCompensationNextYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.overtimeCompensationNextYear -
        rowB.calculated.overtimeCompensationNextYear,
      Cell: ({
        originalData: {
          calculated: { overtimeCompensationNextYear },
        },
      }) => {
        const dayLabel = overtimeCompensationNextYear === 1 ? 'day' : 'days';

        return (
          <div>
            {' '}
            {`${overtimeCompensationNextYear.toLocaleString()}  ${t(`absences:absenceOverview.popOver.${dayLabel}`)}` ||
              '-'}
          </div>
        );
      },
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
  ];
  //#endregion

  return (
    <FilterContextVirtualTable<EmployeeOverviewSearchResultItem>
      id={'timekeepingOverview'}
      className={classNames(classes.root, className)}
      columns={columns}
      data={tableData}
      selectedItems={selectedSearchItems}
      resizable="absolute"
      onSelectionChange={onSelectionChange}
      onCheckEquality={(a, b) =>
        a.data.employeeContactId === b.data.employeeContactId
      }
      noItemsScreen={<FilterResultNoItemsScreen />}
      loading={
        isLoading && {
          type: 'noItems',
        }
      }
      rowsAreSelectable
      actionBarDefaultButtons={
        data?.totalItems > 0
          ? [
              {
                children: t('common:actions.csvExport'),
                iconProp: 'file-arrow-down',
                onClick: handleOnExportToCsvClick,
              },
              {
                children: t('common:actions.excelExport'),
                iconProp: 'file-excel',
                onClick: handleOnExportToExcelClick,
              },
            ]
          : []
      }
      actionBarButtons={[
        {
          children: t('common:actions.excelExportSelection'),
          iconProp: 'file-excel',
          onClick: handleOnExportToExcelClick,
        },
        {
          children: t('common:actions.csvExportSelection'),
          iconProp: 'file-arrow-down',
          onClick: handleOnExportToCsvClick,
        },
      ]}
    />
  );
};

export default HRTimeKeepingOverviewPageTable;
